import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import PageLoader from './components/common/PageLoader';

// Layouts (eagerly loaded for faster initial render)
import PublicLayout from './components/layout/PublicLayout';
import DashboardLayout from './components/layout/DashboardLayout';
import AdminLayout from './components/layout/AdminLayout';

// Public pages
const Home = lazy(() => import('./pages/public/Home'));
const About = lazy(() => import('./pages/public/About'));
const Courses = lazy(() => import('./pages/public/Courses'));
const CourseDetail = lazy(() => import('./pages/public/CourseDetail'));
const Contact = lazy(() => import('./pages/public/Contact'));
const CertifiedTrainers = lazy(() => import('./pages/public/CertifiedTrainers'));
const CertifiedTrainerProfile = lazy(() => import('./pages/public/CertifiedTrainerProfile'));
const Partners = lazy(() => import('./pages/public/Partners'));
const Exams = lazy(() => import('./pages/public/Exams'));
const ExamDetail = lazy(() => import('./pages/public/ExamDetail'));
const Timeline = lazy(() => import('./pages/public/Timeline'));
const VerifyCertificate = lazy(() => import('./pages/public/VerifyCertificate'));
const PremiumNumbers = lazy(() => import('./pages/admin/PremiumNumbers'));
const TakeExam = lazy(() => import('./pages/user/TakeExam'));
const ExamResult = lazy(() => import('./pages/user/ExamResult'));

// Auth pages
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyCode = lazy(() => import('./pages/auth/VerifyCode'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));

// User dashboard pages
const Dashboard = lazy(() => import('./pages/user/Dashboard'));
const MyCourses = lazy(() => import('./pages/user/MyCourses'));
const CourseView = lazy(() => import('./pages/user/CourseView'));
const Profile = lazy(() => import('./pages/user/Profile'));
const Certificates = lazy(() => import('./pages/user/Certificates'));
const BecomeCertifiedTrainer = lazy(() => import('./pages/user/BecomeCertifiedTrainer'));
const Wishlist = lazy(() => import('./pages/user/Wishlist'));
const MyExams = lazy(() => import('./pages/user/MyExams'));
const MyInvoices = lazy(() => import('./pages/user/MyInvoices'));
const PaymentSuccess = lazy(() => import('./pages/user/PaymentSuccess'));

// Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ManageCoursesPage = lazy(() => import('./pages/admin/ManageCoursesPage'));
const ManageTrainersPage = lazy(() => import('./pages/admin/ManageTrainersPage'));
const ManageUsers = lazy(() => import('./pages/admin/ManageUsers'));
const ContactRequests = lazy(() => import('./pages/admin/ContactRequests'));
const ManageEnrollments = lazy(() => import('./pages/admin/ManageEnrollments'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const ManageHomePage = lazy(() => import('./pages/admin/ManageHomePage'));
const ManageTimeline = lazy(() => import('./pages/admin/ManageTimeline'));
const ManagePartners = lazy(() => import('./pages/admin/ManagePartners'));
const SystemLogs = lazy(() => import('./pages/admin/SystemLogs'));
const ManageNavigation = lazy(() => import('./pages/admin/ManageNavigation'));
const ManageBillingPage = lazy(() => import('./pages/admin/ManageBillingPage'));
const DataBackup = lazy(() => import('./pages/admin/DataBackup'));
const ManageExams = lazy(() => import('./pages/admin/ManageExams'));
const ExamEditor = lazy(() => import('./pages/admin/ExamEditor'));
const VideoJobs = lazy(() => import('./pages/admin/VideoJobs'));
const VideoMonitoring = lazy(() => import('./pages/admin/VideoMonitoring'));
const ObjectManagement = lazy(() => import('./pages/admin/ObjectManagement'));
const ManageCertificationsPage = lazy(() => import('./pages/admin/ManageCertificationsPage'));
const TrainerPerformanceDetail = lazy(() => import('./pages/admin/TrainerPerformanceDetail'));
const TrainerFeeStatementDetail = lazy(() => import('./pages/admin/TrainerFeeStatementDetail'));

// Teaching pages
const TeachingDashboard = lazy(() => import('./pages/teaching/TeachingDashboard'));
const TeachingCourses = lazy(() => import('./pages/teaching/TeachingCourses'));
const TeachingCourseEdit = lazy(() => import('./pages/teaching/TeachingCourseEdit'));
const TeachingVideoStatus = lazy(() => import('./pages/teaching/TeachingVideoStatus'));
const TeachingStudents = lazy(() => import('./pages/teaching/TeachingStudents'));
const TeachingMembers = lazy(() => import('./pages/teaching/TeachingMembers'));
const TeachingApprovals = lazy(() => import('./pages/teaching/TeachingApprovals'));
const TeachingMessages = lazy(() => import('./pages/teaching/TeachingMessages'));
const TeachingExams = lazy(() => import('./pages/teaching/TeachingExams'));
const TeachingExamEdit = lazy(() => import('./pages/teaching/TeachingExamEdit'));
const TeachingCertificates = lazy(() => import('./pages/teaching/TeachingCertificates'));
const TeachingExamAttempts = lazy(() => import('./pages/teaching/TeachingExamAttempts'));
const TeachingPromoCodes = lazy(() => import('./pages/teaching/TeachingPromoCodes'));
const StripeConfig = lazy(() => import('./pages/teaching/StripeConfig'));
const TeachingInvoiceSettings = lazy(() => import('./pages/teaching/TeachingInvoiceSettings'));
const TeachingOpenApi = lazy(() => import('./pages/teaching/TeachingOpenApi'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function CertifiedTrainerRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Allow admins and approved certified trainers
  if (user?.role !== 'admin' && !user?.isCertifiedTrainer) {
    return <Navigate to="/become-certified-trainer" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />
          <Route path="/trainers" element={<CertifiedTrainers />} />
          <Route path="/trainers/:id" element={<CertifiedTrainerProfile />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/exams" element={<Exams />} />
          <Route path="/exams/:slug" element={<ExamDetail />} />
          <Route path="/history" element={<Timeline />} />
          <Route path="/verify-certificate" element={<VerifyCertificate />} />
        </Route>

        {/* Auth Routes (standalone, no header/footer) */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-code" element={<VerifyCode />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* User Dashboard Routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/my-courses" element={<MyCourses />} />
          <Route path="/my-courses/:courseId" element={<CourseView />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/certificates" element={<Certificates />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/my-exams" element={<MyExams />} />
          <Route path="/billing" element={<MyInvoices />} />
          <Route path="/my-exams/:examId/take" element={<TakeExam />} />
          <Route path="/my-exams/:examId/result/:attemptId" element={<ExamResult />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/become-certified-trainer" element={<BecomeCertifiedTrainer />} />
        </Route>

        {/* Certified Trainer Dashboard Routes */}
        <Route
          element={
            <CertifiedTrainerRoute>
              <DashboardLayout />
            </CertifiedTrainerRoute>
          }
        >
          <Route path="/my-teaching" element={<TeachingDashboard />} />
          <Route path="/my-teaching/courses" element={<TeachingCourses />} />
          <Route path="/my-teaching/video-status" element={<TeachingVideoStatus />} />
          <Route path="/my-teaching/courses/new" element={<TeachingCourseEdit />} />
          <Route path="/my-teaching/courses/:courseId" element={<TeachingCourseEdit />} />
          <Route path="/my-teaching/courses/:courseId/students" element={<TeachingStudents />} />
          <Route path="/my-teaching/members" element={<TeachingMembers />} />
          <Route path="/my-teaching/exams" element={<TeachingExams />} />
          <Route path="/my-teaching/exams/new" element={<TeachingExamEdit />} />
          <Route path="/my-teaching/exams/:examId" element={<TeachingExamEdit />} />
          <Route path="/my-teaching/exams/:examId/attempts" element={<TeachingExamAttempts />} />
          <Route path="/my-teaching/certificates" element={<TeachingCertificates />} />
          <Route path="/my-teaching/promo-codes" element={<TeachingPromoCodes />} />
          <Route path="/my-teaching/approvals" element={<TeachingApprovals />} />
          <Route path="/my-teaching/messages" element={<TeachingMessages />} />
          <Route path="/my-teaching/stripe-config" element={<StripeConfig />} />
          <Route path="/my-teaching/invoice-settings" element={<TeachingInvoiceSettings />} />
          <Route path="/my-teaching/open-api" element={<TeachingOpenApi />} />
        </Route>

        {/* Admin Routes */}
        <Route
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/courses" element={<Navigate to="/admin/courses/list" replace />} />
          <Route path="/admin/courses/list" element={<ManageCoursesPage />} />
          <Route path="/admin/courses/approvals" element={<ManageCoursesPage />} />
          <Route path="/admin/course-approvals" element={<Navigate to="/admin/courses/approvals" replace />} />
          <Route path="/admin/trainers" element={<Navigate to="/admin/trainers/list" replace />} />
          <Route path="/admin/trainers/list" element={<ManageTrainersPage />} />
          <Route path="/admin/trainers/applications" element={<ManageTrainersPage />} />
          <Route path="/admin/trainers/home-display" element={<ManageTrainersPage />} />
          <Route path="/admin/trainers/performance/:trainerId" element={<TrainerPerformanceDetail />} />
          <Route path="/admin/trainers/performance/:trainerId/fee/:statementId" element={<TrainerFeeStatementDetail />} />
          <Route path="/admin/trainer-applications" element={<Navigate to="/admin/trainers/applications" replace />} />
          <Route path="/admin/trainer-performance" element={<Navigate to="/admin/trainers/list" replace />} />
          <Route path="/admin/trainer-performance/:trainerId" element={<TrainerPerformanceDetail />} />
          <Route path="/admin/users" element={<ManageUsers />} />
          <Route path="/admin/contacts" element={<ContactRequests />} />
          <Route path="/admin/enrollments" element={<ManageEnrollments />} />
          <Route path="/admin/certifications" element={<Navigate to="/admin/certifications/issued" replace />} />
          <Route path="/admin/certifications/issued" element={<ManageCertificationsPage />} />
          <Route path="/admin/certifications/designer" element={<Navigate to="/admin/certifications/templates" replace />} />
          <Route path="/admin/certifications/templates" element={<ManageCertificationsPage />} />
          <Route path="/admin/certifications/templates/new" element={<ManageCertificationsPage />} />
          <Route path="/admin/certifications/templates/:templateId" element={<ManageCertificationsPage />} />
          <Route path="/admin/premium-numbers" element={<PremiumNumbers />} />
          <Route path="/admin/analytics" element={<Analytics />} />
          <Route path="/admin/partners" element={<ManagePartners />} />
          <Route path="/admin/home-page" element={<ManageHomePage />} />
          <Route path="/admin/timeline" element={<ManageTimeline />} />
          <Route path="/admin/navigation" element={<ManageNavigation />} />
          <Route path="/admin/logs" element={<SystemLogs />} />
          <Route path="/admin/billing" element={<Navigate to="/admin/billing/payments" replace />} />
          <Route path="/admin/billing/payments" element={<ManageBillingPage />} />
          <Route path="/admin/billing/promo-codes" element={<ManageBillingPage />} />
          <Route path="/admin/billing/invoice-template" element={<ManageBillingPage />} />
          <Route path="/admin/billing/invoices" element={<ManageBillingPage />} />
          <Route path="/admin/backup" element={<DataBackup />} />
          <Route path="/admin/exams" element={<ManageExams />} />
          <Route path="/admin/exams/new" element={<ExamEditor />} />
          <Route path="/admin/exams/:examId" element={<ExamEditor />} />
          <Route path="/admin/video-jobs" element={<VideoJobs />} />
          <Route path="/admin/video-monitoring" element={<VideoMonitoring />} />
          {/* Legacy URL — keep redirecting so old bookmarks still resolve. */}
          <Route path="/admin/video-failures" element={<Navigate to="/admin/video-monitoring" replace />} />
          <Route path="/admin/object-management" element={<ObjectManagement />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
